import type { CSSProperties, ReactNode } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'

export interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  footer?: ReactNode
}

const overlayStyles: CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(44, 36, 22, 0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 300,
  padding: '16px',
}

const contentStyles: CSSProperties = {
  width: '100%',
  maxWidth: '480px',
  maxHeight: '85vh',
  backgroundColor: 'var(--color-surface)',
  backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
  backgroundBlendMode: 'soft-light',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-elevated)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}

const headerStyles: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 20px',
  borderBottom: 'var(--border-subtle)',
  flexShrink: 0,
}

const titleStyles: CSSProperties = {
  fontFamily: 'var(--font-brand)',
  fontSize: '1.25rem',
  fontWeight: 600,
  color: 'var(--color-ink)',
  margin: 0,
}

const closeButtonStyles: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  padding: 0,
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  color: 'var(--color-ink-tertiary)',
  transition: 'background-color 0.15s ease',
}

const bodyStyles: CSSProperties = {
  flex: 1,
  overflow: 'auto',
  padding: '20px',
}

const footerStyles: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '12px',
  padding: '16px 20px',
  borderTop: 'var(--border-subtle)',
  flexShrink: 0,
}

function CloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M5 5L15 15M15 5L5 15"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

export function Modal({ isOpen, onClose, title, children, footer }: ModalProps) {
  const prefersReducedMotion = useReducedMotion()

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <AnimatePresence>
        {isOpen && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild>
              <motion.div
                style={overlayStyles}
                initial={prefersReducedMotion ? undefined : { opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={prefersReducedMotion ? undefined : { opacity: 0 }}
                transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.2 }}
                data-testid="modal-overlay"
              >
                <Dialog.Content asChild>
                  <motion.div
                    style={contentStyles}
                    initial={prefersReducedMotion ? undefined : { opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={prefersReducedMotion ? undefined : { opacity: 0, scale: 0.97 }}
                    transition={
                      prefersReducedMotion ? { duration: 0 } : { duration: 0.15, ease: 'easeOut' }
                    }
                    onClick={(e) => e.stopPropagation()}
                  >
                    {title ? (
                      <header style={headerStyles}>
                        <Dialog.Title style={titleStyles}>{title}</Dialog.Title>
                        <Dialog.Close asChild>
                          <motion.button
                            type="button"
                            style={closeButtonStyles}
                            aria-label="Close"
                            whileHover={{
                              backgroundColor: 'rgba(44, 36, 22, 0.05)',
                            }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <CloseIcon />
                          </motion.button>
                        </Dialog.Close>
                      </header>
                    ) : (
                      <VisuallyHidden.Root asChild>
                        <Dialog.Title>Dialog</Dialog.Title>
                      </VisuallyHidden.Root>
                    )}
                    <Dialog.Description asChild>
                      <VisuallyHidden.Root>Dialog content</VisuallyHidden.Root>
                    </Dialog.Description>
                    <div style={bodyStyles}>{children}</div>
                    {footer && <footer style={footerStyles}>{footer}</footer>}
                  </motion.div>
                </Dialog.Content>
              </motion.div>
            </Dialog.Overlay>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  )
}
